import { ArtistEntry, AlbumEntry, musicGenres } from '@models/Music'
import axios, { AxiosRequestConfig } from 'axios'

const baseApiConfig: AxiosRequestConfig = {
  responseType: 'json'
}

const client = axios.create({
  baseURL: 'https://musicbrainz.org/ws/2',
  ...baseApiConfig,
})

interface SearchArtistsData {
  artists: ArtistEntry[]
  count: number
}

interface Genre {
  count: number
  name: string
}

interface ReleaseGroup {
  id: string
  title: string
  'primary-type': string
  genres: Genre[]
}

interface LookupData {
  id: string
  name: string
  genres: Genre[]
  'release-groups': ReleaseGroup[]
  count: number
}

const artistNamesToDiscard = [
  '[anonymous]',
  '[unknown]',
  '[unknown unknown]',
  '[unknown] [unknown]',
  '[no artist]',
  '.unknown.',
  '[language instruction]'
]

export const genreMap: Record<string, string[]> = {
  'ELECTRO': [
    'trance', 
    'trip hop', 
    'techno', 
    'alternative dance', 
    'breakbeat', 
    'breakcore', 
    'breaks', 
    'broken beat',  
    'chillout', 
    'chillwave', 
    'chiptune', 
    'club', 
    'dance', 
    'deep house', 
    'drum and bass', 
    'dub', 
    'downtempo', 
    'dub techno', 
    'dubstep', 
    'ebm', 
    'edm', 
    'electro', 
    'electro house', 
    'electronic', 
    'electronica', 
    'hardstyle', 
    'house', 
    'idm', 
    'leftfield', 
    'synthwave', 
    'tech house'
  ],
  'RAP-R-B': [
    'trap', 
    'chopped and screwed', 
    'contemporary r&b', 
    'gangsta rap', 
    'g-funk', 
    'hardcore hip hop', 
    'r&b'
  ],
  'METAL': [
    'viking metal', 
    'trash metal', 
    'technical death metal', 
    'power metal', 
    'nu metal', 
    'alternative metal', 
    'atmospheric black metal', 
    'black metal', 
    'brutal death metal', 
    'death metal', 
    'death-doom metal',  
    'doom metal', 
    'deathcore', 
    'deathgrind', 
    'folk metal', 
    'funk metal', 
    'gothic metal', 
    'grindcore', 
    'groove metal', 
    'heavy metal', 
    'melodic death metal', 
    'metal', 
    'metalcore', 
    'post-metal', 
    'speed metal', 
    'symphonic metal',
    'thrash metal'
  ],
  'JAZZ': [
    'vocal jazz', 
    'acid jazz', 
    'afrobeat', 
    'cool jazz', 
    'contemporary jazz', 
    'avant-garde jazz', 
    'big band', 
    'bossa nova', 
    'contemporary jazz', 
    'free jazz', 
    'fusion', 
    'future jazz', 
    'hard bop', 
    'jazz', 
    'swing'
  ],
  'CHANSON-FRANCAISE': [
    'yé-yé', 
    'chanson française'
  ],
  'HIP-HOP': [
    'hip hop', 
    'alternative hip hop', 
    'east coast hip hop', 
    'pop rap', 
    'nerdcore'
  ],
  'POP': [
    'teen pop',
    'pop',
    'alternative pop',
    'art pop',
    'dance-pop',
    'disco',
    'electropop',
    'europop',
    'folk pop',
    'indian pop',
    'indie pop',
    'j-pop',
    'k-pop',
    'mandopop'
  ],
  'ALTERNATIVE-ROCK': [
    'acid rock', 
    'alternative rock', 
    'arena rock', 
    'boggie rock', 
    'britpop', 
    'experimental rock', 
    'garage rock', 
    'glam', 
    'glam rock', 
    'gothic rock', 
    'pop rock', 
    'shoegaze'
  ],
  'FOLK-ROCK': [
    'roots rock', 
    'soft rock', 
    'folk rock'
  ],
  'ROCK': [
    'progressive rock', 
    'rock', 
    'rock and roll', 
    'acoustic rock', 
    'aor', 
    'art rock', 
    'baroque pop', 
    'canterbury scene',  
    'chamber pop', 
    'classic rock', 
    'comedy rock', 
    'heartland rock', 
    'indie rock', 
    'instrumental rock', 
    'math rock', 
    'neo-progressive rock',  
    'rockabilly', 
    'southern rock', 
    'surf rock', 
    'symphonic rock'
  ],
  'BLUES-SOUL': [
    'blues', 
    'blues rock', 
    'african blues', 
    'blue-eyed soul', 
    'funk', 
    'funk soul', 
    'motown', 
    'neo soul', 
    'pop soul', 
    'smooth soul', 
    'soul', 
    'soul jazz'
  ],
  'FOLK': [
    'alternative folk', 
    'contemporary folk', 
    'folk', 
    'indie folk'
  ],
  'PUNK': [
    'alternative punk', 
    'anti-folk', 
    'dance-punk', 
    'dark wave', 
    'emo', 
    'folk punk', 
    'garage punk', 
    'gothic', 
    'hardcore punk', 
    'horror punk', 
    'mathcore', 
    'melodic hardcore', 
    'neo-psychedelia', 
    'pop punk', 
    'post-hardcore', 
    'post-punk', 
    'ska punk'
  ],
  'AMBIANCE': [
    'ambiant', 
    'ambiant pop', 
    'new age', 
    'celtic music', 
    'easy listening', 
    'lo-fi', 
    'instrumental', 
    'lounge', 
    'minimal'
  ],
  'COUNTRY': [
    'americana', 
    'country', 
    'bluegrass', 
    'country pop', 
    'country rock', 
    'gothic country', 
    'nashville sound'
  ],
  'AVANT-GARDE-EXPERIMENTAL': [
    'avant-garde', 
    'experimental', 
    'no wave'
  ],
  'LATIN-MUSIC': [
    'bachata', 
    'cumbia', 
    'fado', 
    'flamenco', 
    'latin', 
    'latin pop', 
    'latin rock', 
    'merengue', 
    'mpb', 
    'reggaeton'
  ],
  'BALLAD': [
    'ballad'
  ],
  'CLASSIQUE-OPERA': [
    'baroque', 
    'bolero', 
    'classical', 
    'classical crossover', 
    'modern classical', 
    'opera', 
    'orchestral'
  ],
  'AFRICAN': [
    'compas', 
    'dancehall'
  ],
  'CHRISTMAS-MUSIC': [
    'christmas music'
  ],
  'NEW-WAVE': [
    'coldwave', 
    'new wave', 
    'minimal wave', 
    'synth-pop'
  ],
  'INDIE': [
    'indie'
  ],
  'RELIGIOUS-MUSIC': [
    'contemporary christian', 
    'christian rock'
  ],
  'RAGGAE': [
    'ragga', 
    'raggae', 
    'ska'
  ],
  'HARD-ROCK': [
    'grunge', 
    'hard rock', 
    'post-grunge', 
    'post rock', 
    'stoner rock'
  ],
  'INDUSTRIAL': [
    'industrial', 
    'industrial metal', 
    'industrial rock', 
    'neofolk'
  ],
  'OTHERS': [
    'comedy', 
    'dark ambient', 
    'medieval', 
    'musical', 
    'noise', 
    'non-music', 
    'schlager', 
    'singer-songwriter', 
    'unknown', 
    'spoken word', 
    'zeuhl', 
    'dreampop'
  ]
}

// eslint-disable-next-line no-useless-escape
const luceneAndElasticSpecialCharacters = /([\!\*\+\-\=\<\>\&\|\(\)\[\]\{\}\^\~\?\:\\/"])/g

const mapGenre = (name: string): string => {
  const lowerName = name.toLowerCase()
  for (const k in genreMap) {
    const foundGenre = genreMap[k].find(g => g === lowerName)
    if (foundGenre) {
      return k
    }
  }
  return ''
}

const searchArtist = async (artist: string): Promise<ArtistEntry[]> => {
  const artistTerm = artist.replace(luceneAndElasticSpecialCharacters, '\\$1')

  const searchResults = await client.get<SearchArtistsData>('/artist', {
    params: {
      fmt: 'json',
      query: `artist:${artistTerm}*`,
    },
  })

  return searchResults.data.artists.filter((a) => !artistNamesToDiscard.includes(a.name.toLowerCase()))
}

const sortGenre = (a: Genre, b: Genre) => a.count > b.count ? -1 : 1

const getGenreFromReleaseGroup = (releaseGroup: ReleaseGroup): string => {
  if (!(releaseGroup.genres && releaseGroup.genres.length)) return 'OTHERS'
  return getGenre(releaseGroup.genres)
}

const getGenre = (artistGenres: Genre[]): string => {
  if(artistGenres.length === 0) return ''
  const orderedGenres = [...artistGenres].sort(sortGenre)
  const genres = orderedGenres.filter(g => musicGenres.includes(g.name))
  const genre = genres.length ? genres[0] : orderedGenres[0]
  return mapGenre(genre.name)
}

const lookupAlbums = async (artistId: string, term: string, artist: string): Promise<AlbumEntry[]> => {
  if (!artistId) {
    return []
  }

  const searchResults = await client.get<LookupData>(`/artist/${artistId}`, {
    params: {
      fmt: 'json',
      inc: 'release-groups+genres'
    }
  })

  const albums = searchResults.data['release-groups']
  .filter(rg => rg['primary-type'] === 'Album')

  if(albums.length === 0) {
    return [{
      id: searchResults.data.id,
      term,
      artist,
      title: searchResults.data.name,
      genre: getGenre(searchResults.data.genres),
      artistId,
      artworkUrl: '',
    }]
  }

  return albums
    .map(rg => {
      return {
        id: rg.id,
        term,
        artist,
        title: rg.title,
        genre: getGenreFromReleaseGroup(rg),
        artistId,
        artworkUrl: `https://coverartarchive.org/release-group/${rg.id}/front-250`
      }
    })
}

export default {
  searchArtist,
  lookupAlbums
}
